import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import { fetchAllData } from '../lib/api';
import { SongCard } from '../components/SongCard';
import { Link } from 'react-router-dom';
import { Artist, Voicebank, Song } from '../types/models';
import { getArtistNames, songBelongsToArtist } from '../utils/artistUtils';

type SearchCategory = 'all' | 'songs' | 'artists' | 'voicebanks';

export function Search() {
  const { data, isLoading, error } = useQuery(['appData'], fetchAllData);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<SearchCategory>('all');
  const [searchResults, setSearchResults] = useState<{
    songs: Song[];
    artists: Artist[];
    voicebanks: Voicebank[];
  }>({
    songs: [],
    artists: [],
    voicebanks: []
  });

  // Initialize search query from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
    }
  }, [location.search]);

  // Update URL when search query changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('q', value.trim());
      navigate(`/search?${searchParams.toString()}`, { replace: true });
    } else {
      navigate('/search', { replace: true });
    }
  };

  // Perform search when query or category changes
  useEffect(() => {
    if (!data || !searchQuery.trim()) {
      setSearchResults({ songs: [], artists: [], voicebanks: [] });
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    
    // Filter songs
    const filteredSongs = activeCategory === 'all' || activeCategory === 'songs'
      ? data.songs.filter((song: Song) => 
          song.title.toLowerCase().includes(query) ||
          getArtistName(song.artistId).toLowerCase().includes(query) ||
          getVoicebankName(song.synthId).toLowerCase().includes(query)
        )
      : [];
    
    // Filter artists
    const filteredArtists = activeCategory === 'all' || activeCategory === 'artists'
      ? data.artists.filter((artist: Artist) => 
          artist.name.toLowerCase().includes(query) ||
          artist.description.toLowerCase().includes(query)
        )
      : [];
    
    // Filter voicebanks
    const filteredVoicebanks = activeCategory === 'all' || activeCategory === 'voicebanks'
      ? data.voicebanks.filter((voicebank: Voicebank) => 
          voicebank.name.toLowerCase().includes(query) ||
          voicebank.engine.toLowerCase().includes(query) ||
          voicebank.language.toLowerCase().includes(query)
        )
      : [];
    
    setSearchResults({
      songs: filteredSongs,
      artists: filteredArtists,
      voicebanks: filteredVoicebanks
    });
  }, [searchQuery, activeCategory, data]);

  const getArtistName = (artistId: string) => {
    return data ? getArtistNames(artistId, data.artists) : 'Unknown Artist';
  };

  const getVoicebankName = (synthId: string) => {
    return data?.voicebanks.find((v: Voicebank) => v.id === synthId)?.name || 'Unknown Voicebank';
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-white/10 rounded-lg w-full max-w-md mx-auto mb-8" />
          <div className="flex gap-2 justify-center mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 w-24 bg-white/10 rounded-full" />
            ))}
          </div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-white/10 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <h2 className="font-outfit text-2xl text-pearl-white mb-4">Error loading data</h2>
        <p className="text-slate-grey mb-6">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-naia-pink text-pearl-white rounded-lg hover:bg-soft-violet transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const hasResults = searchResults.songs.length > 0 || 
                    searchResults.artists.length > 0 || 
                    searchResults.voicebanks.length > 0;

  const hasNoQuery = !searchQuery.trim();

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {/* Search header */}
      <div className="mb-8">
        <h1 className="font-outfit text-2xl font-semibold text-pearl-white mb-4">Search</h1>
        
        {/* Search input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="text-slate-grey" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search for songs, artists or voicebanks..."
            className="w-full bg-white/5 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-pearl-white placeholder-slate-grey focus:outline-none focus:ring-2 focus:ring-naia-pink/50 focus:border-naia-pink/50"
            data-search-input
            data-keyboard-shortcut="Cmd+K"
          />
        </div>
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveCategory('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
            activeCategory === 'all'
              ? 'bg-naia-pink text-pearl-white'
              : 'bg-white/5 text-slate-grey hover:bg-white/10'
          }`}
        >
          <TuneIcon fontSize="small" />
          <span>All</span>
        </button>
        
        <button
          onClick={() => setActiveCategory('songs')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
            activeCategory === 'songs'
              ? 'bg-naia-pink text-pearl-white'
              : 'bg-white/5 text-slate-grey hover:bg-white/10'
          }`}
        >
          <MusicNoteIcon fontSize="small" />
          <span>Songs</span>
        </button>
        
        <button
          onClick={() => setActiveCategory('artists')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
            activeCategory === 'artists'
              ? 'bg-naia-pink text-pearl-white'
              : 'bg-white/5 text-slate-grey hover:bg-white/10'
          }`}
        >
          <PersonIcon fontSize="small" />
          <span>Artists</span>
        </button>
        
        <button
          onClick={() => setActiveCategory('voicebanks')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
            activeCategory === 'voicebanks'
              ? 'bg-naia-pink text-pearl-white'
              : 'bg-white/5 text-slate-grey hover:bg-white/10'
          }`}
        >
          <KeyboardVoiceIcon fontSize="small" />
          <span>Voicebanks</span>
        </button>
      </div>

      {/* Search results */}
      <div>
        {/* Initial state */}
        {hasNoQuery && (
          <div className="text-center py-12">
            <SearchIcon sx={{ fontSize: 48 }} className="text-slate-grey mb-4" />
            <p className="text-lg font-medium text-pearl-white">Start typing to search</p>
            <p className="text-sm text-slate-grey mt-2">
              Search for songs, artists, or voicebanks
            </p>
          </div>
        )}

        {/* No results state */}
        {!hasNoQuery && !hasResults && (
          <div className="text-center py-12">
            <SearchIcon sx={{ fontSize: 48 }} className="text-slate-grey mb-4" />
            <p className="text-lg font-medium text-pearl-white">No results found</p>
            <p className="text-sm text-slate-grey mt-2">
              Try a different search term or category
            </p>
          </div>
        )}

        {/* Songs results */}
        {searchResults.songs.length > 0 && (activeCategory === 'all' || activeCategory === 'songs') && (
          <div className="mb-8">
            <h2 className="font-outfit text-xl font-medium text-pearl-white mb-4">
              Songs
              <span className="text-slate-grey text-sm ml-2">
                ({searchResults.songs.length})
              </span>
            </h2>
            <div className="space-y-2">
              {searchResults.songs.map(song => (
                <SongCard
                  key={song.id}
                  song={song}
                  artist={getArtistName(song.artistId)}
                  voicebank={getVoicebankName(song.synthId)}
                  layout="list"
                />
              ))}
            </div>
          </div>
        )}

        {/* Artists results */}
        {searchResults.artists.length > 0 && (activeCategory === 'all' || activeCategory === 'artists') && (
          <div className="mb-8">
            <h2 className="font-outfit text-xl font-medium text-pearl-white mb-4">
              Artists
              <span className="text-slate-grey text-sm ml-2">
                ({searchResults.artists.length})
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {searchResults.artists.map(artist => (
                <Link 
                  key={artist.id}
                  to={`/artist/${artist.id}`}
                  className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 hover:shadow-lg hover:shadow-naia-pink/5 flex items-center gap-3"
                >
                  <img 
                    src={artist.imageUrl} 
                    alt={artist.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-outfit text-pearl-white font-medium">{artist.name}</h3>
                    <p className="text-slate-grey text-sm truncate">
                      {artist.synthIds.length} voicebank{artist.synthIds.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Voicebanks results */}
        {searchResults.voicebanks.length > 0 && (activeCategory === 'all' || activeCategory === 'voicebanks') && (
          <div>
            <h2 className="font-outfit text-xl font-medium text-pearl-white mb-4">
              Voicebanks
              <span className="text-slate-grey text-sm ml-2">
                ({searchResults.voicebanks.length})
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {searchResults.voicebanks.map(voicebank => (
                <Link 
                  key={voicebank.id}
                  to={`/voicebank/${voicebank.id}`}
                  className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 hover:shadow-lg hover:shadow-naia-pink/5"
                >
                  <div className="flex items-center justify-center w-10 h-10 bg-naia-pink/20 rounded-full mb-3">
                    <KeyboardVoiceIcon className="text-naia-pink" />
                  </div>
                  <h3 className="font-outfit text-pearl-white font-medium">{voicebank.name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-slate-grey">
                      {voicebank.engine}
                    </span>
                    <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-slate-grey">
                      {voicebank.language}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
